import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import CreatePlatformUserForm, { type CreatePlatformUserPayload } from '@/components/CreatePlatformUserForm';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal';
import EditPlatformUserModal from '@/components/EditPlatformUserModal';
import ResetPasswordTeamMemberModal from '@/components/ResetPasswordTeamMemberModal';
import { ErrorBanner } from '@/components/ui';
import PageHeader from '@/components/PageHeader';
import { AdminPageSections, CollapsiblePanel } from '@/components/common';
import { DataTable, TruncatedCell, TableActionBar, TABLE_ACTION_MIN_WIDTH, tableActionsColumnClass, type DataTableColumn } from '@/components/table';
import { exportTableCsv } from '@/utils/exportTableCsv';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { platformRoleLabel, formatOrganizationNames } from '@/utils/workspaceRoles';
import type { User } from '@/types';

const BOOTSTRAP_OWNER_EMAIL = 'admin@civic-alert.local';

export default function ManagementUsersPage() {
  const { toast } = useToast();
  const { me } = useMe();
  const { workspaceVersion } = useWorkspace();
  const [searchParams] = useSearchParams();
  const canManage = me?.permissions.canManageUsers ?? false;
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);

  const load = useCallback(async () => {
    setUsers(await api.users.listPlatform());
  }, []);

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [load, workspaceVersion]);

  useEffect(() => {
    const userId = searchParams.get('user');
    if (!userId || users.length === 0) return;
    const match = users.find((user) => user.id === userId);
    if (match) setEditUser(match);
  }, [searchParams, users]);

  const hasBootstrapOwner = useMemo(
    () => users.some((u) => u.email === BOOTSTRAP_OWNER_EMAIL && u.role === 'owner'),
    [users],
  );

  async function createPlatformUser(payload: CreatePlatformUserPayload) {
    setCreating(true);
    setError('');
    try {
      await api.users.createPlatform({
        email: payload.email,
        password: payload.password,
        name: payload.name,
        role: payload.role,
        is_enabled: payload.is_enabled,
      });
      toast('Platform user created', 'success');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
      throw err;
    } finally {
      setCreating(false);
    }
  }

  const toggleEnabled = useCallback(
    async (u: User) => {
      if (u.id === me?.id) {
        toast('You cannot disable your own account', 'error');
        return;
      }
      try {
        await api.users.update(u.id, { is_enabled: !u.is_enabled });
        toast(u.is_enabled ? 'User disabled' : 'User enabled', 'info');
        await load();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Update failed', 'error');
      }
    },
    [toast, load, me?.id],
  );

  const teamColumns = useMemo<DataTableColumn<User>[]>(
    () => [
      {
        id: 'email',
        header: 'Email',
        width: 200,
        minWidth: 140,
        maxWidth: 280,
        sortable: true,
        sortValue: (u) => u.email,
        csvValue: (u) => u.email,
        render: (u) => <TruncatedCell value={u.email} />,
      },
      {
        id: 'name',
        header: 'Name',
        width: 140,
        minWidth: 100,
        maxWidth: 220,
        render: (u) => <TruncatedCell value={u.name} />,
      },
      {
        id: 'role',
        header: 'Platform role',
        width: 130,
        minWidth: 100,
        maxWidth: 160,
        resizable: false,
        render: (u) => (
          <TruncatedCell value={platformRoleLabel(u.role ?? 'none')} tooltip={false} />
        ),
      },
      {
        id: 'organizations',
        header: 'Workspace memberships',
        width: 200,
        minWidth: 140,
        maxWidth: 280,
        render: (u) => (
          <TruncatedCell
            value={formatOrganizationNames(u.organizationNames)}
            tooltip={u.organizationNames && u.organizationNames.length > 1}
          />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: 100,
        minWidth: 80,
        maxWidth: 120,
        resizable: false,
        render: (u) => (u.is_enabled ? 'Enabled' : 'Disabled'),
      },
      {
        id: 'actions',
        header: 'Actions',
        width: TABLE_ACTION_MIN_WIDTH.team,
        minWidth: TABLE_ACTION_MIN_WIDTH.team,
        resizable: false,
        actions: true,
        className: tableActionsColumnClass('team'),
        render: (u) =>
          canManage ? (
            <TableActionBar
              danger={
                u.id !== me?.id ? (
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeleteId(u.id)}>
                    Remove
                  </button>
                ) : undefined
              }
            >
              <button type="button" className="btn btn-sm" onClick={() => setEditUser(u)}>
                Edit
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setResetPasswordUser(u)}>
                Reset password
              </button>
              {u.id !== me?.id && (
                <button type="button" className="btn btn-sm" onClick={() => toggleEnabled(u)}>
                  {u.is_enabled ? 'Disable' : 'Enable'}
                </button>
              )}
            </TableActionBar>
          ) : null,
      },
    ],
    [canManage, me?.id, toggleEnabled],
  );

  return (
    <div className="admin-page admin-page--team">
      <PageHeader
        title="Platform users"
        subtitle="Scoutly staff accounts for platform administration. Workspace memberships are managed separately from each workspace Users page."
      />

      {error ? <ErrorBanner message={error} onDismiss={() => setError('')} /> : null}

      {hasBootstrapOwner && (
        <div className="alert" role="status">
          Default local owner exists. Change password before real deployment.
        </div>
      )}

      {canManage && (
        <AdminPageSections>
          <CollapsiblePanel id="create-platform-user" storageScope="platform-users-page" title="New platform user" defaultExpanded>
            <CreatePlatformUserForm creating={creating} onSubmit={createPlatformUser} />
          </CollapsiblePanel>

          <CollapsiblePanel id="platform-users-list" storageScope="platform-users-page" title="Scoutly staff" badge={users.length} defaultExpanded unmountWhenCollapsed>
            <DataTable
              columns={teamColumns}
              rows={users}
              getRowKey={(u) => u.id}
              columnStorageKey="platform-users"
              toolbar={{
                searchPlaceholder: 'Filter platform users…',
                onRefresh: () => load(),
                onExportCsv: () => exportTableCsv(teamColumns, users, 'platform-users.csv'),
              }}
            />
          </CollapsiblePanel>
        </AdminPageSections>
      )}

      {!canManage && (
        <div className="card section">
          <DataTable
            columns={teamColumns}
            rows={users}
            getRowKey={(u) => u.id}
            columnStorageKey="platform-users"
            toolbar={{
              searchPlaceholder: 'Filter platform users…',
              onRefresh: () => load(),
              onExportCsv: () => exportTableCsv(teamColumns, users, 'platform-users.csv'),
            }}
          />
        </div>
      )}

      {deleteId && (
        <ConfirmDeleteModal
          entityType="user"
          entityId={deleteId}
          onClose={() => setDeleteId(null)}
          onDone={(msg) => {
            toast(msg, 'success');
            load();
          }}
        />
      )}

      {editUser && (
        <EditPlatformUserModal
          user={editUser}
          currentUserId={me?.id}
          onClose={() => setEditUser(null)}
          onSaved={(msg) => {
            toast(msg, 'success');
            setEditUser(null);
            void load();
          }}
        />
      )}

      {resetPasswordUser && (
        <ResetPasswordTeamMemberModal
          user={resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
          onSaved={(msg) => {
            toast(msg, 'success');
          }}
        />
      )}
    </div>
  );
}
